import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress} from '@mui/material';
import Indicator from '../../components/Indicator';
import MultiIndicator from '../../components/MultiIndicator';
import Section from '../../components/Section';
import {fetchZeroNeetSlugs} from '../utils/helpers';
import {buildDashboard1Slugs} from '../utils/slugs';
import {ZeroNeetDashboardData} from '../utils/types';

interface Props {
    container: string;
    uoaId: string;
    survey499Id: string;
    survey500Id: string;
}

const LABELS = {uoa: 'Il vostro progetto', average: 'Media di tutti i progetti'};

interface SimpleSection {
    title: string;
    keys: string[];
}

interface GroupedSection {
    title: string;
    pair: {key: string; label: string}[];
}

const isGrouped = (section: SimpleSection | GroupedSection): section is GroupedSection => 'pair' in section;

const ACTIVATION_TYPE_LABELS = {
    lavoro: 'Inserimento nel mercato del lavoro',
    studi: 'Ripresa degli studi',
};

const SECTIONS: (SimpleSection | GroupedSection)[] = [
    {title: 'Sensibilizzazione', keys: ['ind_1_1']},
    {title: 'Ingaggio e adesione dei NEET', keys: ['ind_1_2', 'ind_1_3']},
    {title: 'Avvio dei percorsi di attivazione', keys: ['ind_1_4']},
    {title: 'Esiti occupazionali e formativi sul totale degli aderenti', keys: ['ind_1_5']},
    {
        title: 'Esiti occupazionali e formativi sul totale degli intercettati',
        pair: [
            {key: 'ind_1_6', label: ACTIVATION_TYPE_LABELS.lavoro},
            {key: 'ind_1_7', label: ACTIVATION_TYPE_LABELS.studi},
        ],
    },
    {
        title: 'Esiti occupazionali e formativi rispetto alle previsioni iniziali',
        pair: [
            {key: 'ind_1_8', label: ACTIVATION_TYPE_LABELS.lavoro},
            {key: 'ind_1_9', label: ACTIVATION_TYPE_LABELS.studi},
        ],
    },
];

const Dashboard1 = ({container, uoaId, survey499Id, survey500Id}: Props) => {
    const slugs = buildDashboard1Slugs(Number(survey499Id), Number(survey500Id));

    const {data, isLoading, isError} = useQuery<ZeroNeetDashboardData>({
        queryKey: ['zero-neet-dashboard1', container, uoaId, survey499Id, survey500Id],
        queryFn: () => fetchZeroNeetSlugs(container, uoaId, slugs),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            {SECTIONS.map((section) => (
                <Section key={section.title} title={section.title}>
                    {isGrouped(section) ? (
                        <MultiIndicator
                            title={slugs.find((slug) => slug.key === section.pair[0].key)?.title ?? ''}
                            tooltip={slugs.find((slug) => slug.key === section.pair[0].key)?.tooltip ?? ''}
                            items={section.pair.map(({key, label}) => ({label, uoa: data[key].uoa, average: data[key].average}))}
                            maxOverride={100}
                            seriesLabels={LABELS}
                            stacked
                        />
                    ) : (
                        slugs
                            .filter((slug) => section.keys.includes(slug.key))
                            .map((slug) => (
                                <Indicator
                                    key={slug.key}
                                    roman={slug.roman ?? ''}
                                    title={slug.title}
                                    tooltip={slug.tooltip}
                                    data={data[slug.key]}
                                    maxOverride={100}
                                    labels={LABELS}
                                />
                            ))
                    )}
                </Section>
            ))}
        </Box>
    );
};

export default Dashboard1;
